/**
 * Danish relative time: "4d", "2t", "I dag". Tuned for compact table cells.
 *
 *   relativeTime(date, now) ∈ { "I dag", "I går", "Nt" (hours), "Nd" (days),
 *                               "Nu" (weeks), "Nmd" (months), "Nå" (years) }
 */

export function relativeTime(
  value: Date | string | null | undefined,
  now: Date = new Date(),
): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '—';

  const diffMs = now.getTime() - d.getTime();
  if (diffMs < 0) return formatFuture(-diffMs);

  const mins = Math.floor(diffMs / 60_000);
  const hours = Math.floor(diffMs / 3_600_000);
  const days = Math.floor(diffMs / 86_400_000);

  if (mins < 60) return 'I dag';
  if (sameCalendarDay(d, now)) return 'I dag';
  if (isYesterday(d, now)) return 'I går';
  if (hours < 24) return `${hours}t`;
  if (days < 7) return `${days}d`;
  if (days < 30) return `${Math.floor(days / 7)}u`;
  if (days < 365) return `${Math.floor(days / 30)}md`;
  return `${Math.floor(days / 365)}å`;
}

function formatFuture(absMs: number): string {
  const days = Math.floor(absMs / 86_400_000);
  if (days === 0) return 'I dag';
  if (days === 1) return 'I morgen';
  return `om ${days}d`;
}

function sameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isYesterday(a: Date, b: Date): boolean {
  const yesterday = new Date(b);
  yesterday.setDate(b.getDate() - 1);
  return sameCalendarDay(a, yesterday);
}

/** Activity-dot color by recency. green <5d, amber 5-10d, red >10d. */
export function activityDotColor(
  value: Date | string | null | undefined,
  now: Date = new Date(),
): 'green' | 'amber' | 'red' | 'grey' {
  if (!value) return 'grey';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return 'grey';
  const days = (now.getTime() - d.getTime()) / 86_400_000;
  if (days < 5) return 'green';
  if (days < 10) return 'amber';
  return 'red';
}

/**
 * Verbose Danish relative-time used by note timestamps, shoot recency,
 * and activity-log entries (V2 decoupling PR 2b). Compared to the terse
 * `relativeTime` (used in dense table cells), this returns full sentences:
 *
 *   <1 day  → "i dag" / "i går"
 *   <7 days → "N dage siden" (or "1 dag siden")
 *   <28 days → "N uger siden" (or "1 uge siden")
 *   ≥28 days → "5. apr 2026" (Danish short date)
 */
export function formatRelativeTime(
  value: Date | string | null | undefined,
  now: Date = new Date(),
): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '—';

  const diffMs = now.getTime() - d.getTime();
  if (diffMs < 0) {
    return formatLongDate(d);
  }

  const days = Math.floor(diffMs / 86_400_000);
  if (days === 0) return 'i dag';
  if (days === 1) return 'i går';
  if (days < 7) return `${days} dage siden`;
  if (days < 28) {
    const weeks = Math.floor(days / 7);
    return weeks === 1 ? '1 uge siden' : `${weeks} uger siden`;
  }
  return formatLongDate(d);
}

function formatLongDate(d: Date): string {
  return new Intl.DateTimeFormat('da-DK', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d);
}

/**
 * Future- + past-tense Danish relative time used by the v3 ProjectCard:
 *   "om få minutter" / "om 3 t" / "for 2 t siden"
 *   "i morgen" / "i går"
 *   "om 3 dage" / "for 5 dage siden"
 *   "om 2 uger" / "for 3 uger siden"
 *   "om 4 mdr" / "for 6 mdr siden"
 */
export function relativeDanish(
  value: Date | string | null | undefined,
  now: Date = new Date(),
): string {
  if (!value) return '—';
  const target = typeof value === 'string' ? new Date(value) : value;
  if (!(target instanceof Date) || Number.isNaN(target.getTime())) return '—';

  const diffMs = target.getTime() - now.getTime();
  const abs = Math.abs(diffMs);
  const future = diffMs >= 0;
  const HOUR = 3_600_000;
  const DAY = 86_400_000;

  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const startOfTarget = new Date(target);
  startOfTarget.setHours(0, 0, 0, 0);
  const dayDiff = Math.round((startOfTarget.getTime() - startOfToday.getTime()) / DAY);

  if (dayDiff === 0) {
    if (abs < HOUR) return future ? 'om få minutter' : 'lige før';
    return future ? `om ${Math.round(abs / HOUR)} t` : `for ${Math.round(abs / HOUR)} t siden`;
  }
  if (dayDiff === 1) return 'i morgen';
  if (dayDiff === -1) return 'i går';
  if (dayDiff > 0 && dayDiff < 7) return `om ${dayDiff} dage`;
  if (dayDiff < 0 && dayDiff > -7) return `for ${-dayDiff} dage siden`;
  if (dayDiff > 0 && dayDiff < 30) return `om ${Math.round(dayDiff / 7)} uger`;
  if (dayDiff < 0 && dayDiff > -30) return `for ${Math.round(-dayDiff / 7)} uger siden`;
  if (dayDiff > 0) return `om ${Math.round(dayDiff / 30)} mdr`;
  return `for ${Math.round(-dayDiff / 30)} mdr siden`;
}

/** Short Danish date: "20. apr". */
export function formatShortDateDanish(value: Date | string | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '—';
  const months = [
    'jan',
    'feb',
    'mar',
    'apr',
    'maj',
    'jun',
    'jul',
    'aug',
    'sep',
    'okt',
    'nov',
    'dec',
  ];
  return `${d.getDate()}. ${months[d.getMonth()]}`;
}
